import { X } from 'lucide-react';

import { Button } from '@/components/ui';
import { cn } from '@/lib/cn';

export interface MapFilters {
  statuses: string[];
  technologies: string[];
  geographies: string[];
  minCapacity: number | null;
  maxCapacity: number | null;
  dateFrom: string | null;
  dateTo: string | null;
}

export const EMPTY_FILTERS: MapFilters = {
  statuses: [],
  technologies: [],
  geographies: [],
  minCapacity: null,
  maxCapacity: null,
  dateFrom: null,
  dateTo: null,
};

interface MapFilterPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: MapFilters;
  onFiltersChange: (filters: MapFilters) => void;
}

const STATUSES = [
  'Planned',
  'Under Construction',
  'Commissioned',
  'Operational',
  'Mothballed',
  'Decommissioned',
];

/**
 * Slide-in filter panel (320px) per Spec S05 §"Filter panel".
 *
 * The MVP filters cover the highest-value queries: status, technology,
 * geography, capacity range, date range. Stakeholder + advanced filters
 * (financial value, qualitative carbon intensity, inferred toggle) land in v2.
 */
export function MapFilterPanel({
  open,
  onOpenChange,
  filters,
  onFiltersChange,
}: MapFilterPanelProps) {
  const update = (patch: Partial<MapFilters>) =>
    onFiltersChange({ ...filters, ...patch });

  return (
    <div
      className={cn(
        'absolute left-0 top-0 z-20 h-full w-80 transform overflow-y-auto border-r border-slate-200 bg-white shadow-lg transition-transform duration-200',
        open ? 'translate-x-0' : '-translate-x-full',
      )}
      aria-hidden={!open}
    >
      <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2">
        <h3 className="text-h3 text-brand-900">Filters</h3>
        <Button variant="ghost" size="icon" aria-label="Close filters" onClick={() => onOpenChange(false)}>
          <X className="h-4 w-4" aria-hidden />
        </Button>
      </div>

      <div className="space-y-4 px-3 py-3">
        <ChipSection
          title="Status"
          options={STATUSES}
          selected={filters.statuses}
          onChange={(values) => update({ statuses: values })}
        />

        <RangeField
          title="Capacity (MW)"
          minValue={filters.minCapacity}
          maxValue={filters.maxCapacity}
          onChange={(min, max) => update({ minCapacity: min, maxCapacity: max })}
        />

        <DateRangeField
          title="Commercial Operation Date"
          fromValue={filters.dateFrom}
          toValue={filters.dateTo}
          onChange={(from, to) => update({ dateFrom: from, dateTo: to })}
        />

        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onFiltersChange(EMPTY_FILTERS)}
          >
            Clear all
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────

function ChipSection({
  title,
  options,
  selected,
  onChange,
}: {
  title: string;
  options: string[];
  selected: string[];
  onChange: (values: string[]) => void;
}) {
  const toggle = (option: string) => {
    if (selected.includes(option)) {
      onChange(selected.filter((s) => s !== option));
    } else {
      onChange([...selected, option]);
    }
  };
  return (
    <div>
      <p className="mb-1 text-h4 text-slate-700">{title}</p>
      <div className="flex flex-wrap gap-1">
        {options.map((option) => {
          const active = selected.includes(option);
          return (
            <button
              key={option}
              type="button"
              onClick={() => toggle(option)}
              className={cn(
                'rounded-full border px-2 py-0.5 text-small',
                active
                  ? 'border-accent bg-accent text-white'
                  : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-100',
              )}
              aria-pressed={active}
            >
              {option}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function RangeField({
  title,
  minValue,
  maxValue,
  onChange,
}: {
  title: string;
  minValue: number | null;
  maxValue: number | null;
  onChange: (min: number | null, max: number | null) => void;
}) {
  return (
    <div>
      <p className="mb-1 text-h4 text-slate-700">{title}</p>
      <div className="grid grid-cols-2 gap-2">
        <input
          type="number"
          inputMode="numeric"
          placeholder="Min"
          value={minValue ?? ''}
          onChange={(e) => onChange(parseNullableInt(e.target.value), maxValue)}
          className="h-8 rounded border border-slate-300 px-2 text-small"
        />
        <input
          type="number"
          inputMode="numeric"
          placeholder="Max"
          value={maxValue ?? ''}
          onChange={(e) => onChange(minValue, parseNullableInt(e.target.value))}
          className="h-8 rounded border border-slate-300 px-2 text-small"
        />
      </div>
    </div>
  );
}

function DateRangeField({
  title,
  fromValue,
  toValue,
  onChange,
}: {
  title: string;
  fromValue: string | null;
  toValue: string | null;
  onChange: (from: string | null, to: string | null) => void;
}) {
  return (
    <div>
      <p className="mb-1 text-h4 text-slate-700">{title}</p>
      <div className="grid grid-cols-2 gap-2">
        <input
          type="date"
          value={fromValue ?? ''}
          onChange={(e) => onChange(e.target.value || null, toValue)}
          className="h-8 rounded border border-slate-300 px-2 text-small"
        />
        <input
          type="date"
          value={toValue ?? ''}
          onChange={(e) => onChange(fromValue, e.target.value || null)}
          className="h-8 rounded border border-slate-300 px-2 text-small"
        />
      </div>
    </div>
  );
}

function parseNullableInt(value: string): number | null {
  if (!value.trim()) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}
